"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { trackedFetch } from "@/lib/api/trackedFetch";

const COOKIE_NAME = "cookie_consent";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export function CookieConsentBanner() {
  const t = useTranslations("cookie-consent");
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setMounted(true);
    const existing = getCookie(COOKIE_NAME);
    if (!existing) {
      setVisible(true);
    }
  }, []);

  if (!mounted || !visible) return null;

  const handleConsent = async (consent: "accepted" | "essential_only") => {
    setSubmitting(true);
    try {
      await trackedFetch("/api/cookie-consent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ consent }),
      });
    } catch {
      // Set cookie client-side as fallback
      document.cookie = `${COOKIE_NAME}=${consent}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    }
    setVisible(false);
    setSubmitting(false);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-60 p-4 sm:p-6">
      <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-2xl p-5 shadow-lg">
        <p className="text-sm text-gray-700 mb-4">
          {t("message")}{" "}
          <Link href="/privacy" className="underline hover:text-gray-900">
            {t("learnMore")}
          </Link>
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleConsent("accepted")}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 transition-colors"
          >
            {t("accept")}
          </button>
          <button
            onClick={() => handleConsent("essential_only")}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:bg-gray-50 transition-colors"
          >
            {t("essentialOnly")}
          </button>
        </div>
      </div>
    </div>
  );
}
